import { useCallback, useEffect, useRef, useState } from "react";
import { StringParam, useQueryParams } from "use-query-params";

import { ContentWrapper } from "../../common/styledComponents";
import { EMAIL_PARAM_NAME } from "../../hooks/useEmailParam";
import { useIsWithinTabs } from "../../hooks/useWithinTabs";
import { getDefaultZmDateFilters } from "./defaultDateFilters";
import {
  seedZmListState,
  toZmListQuery,
  ZM_LIST_QUERY_CONFIG,
} from "./listQueryParams";
import SubNavigation, { ZM_TABS } from "./SubNavigation";
import ZmFilters from "./ZmFilters";
import ZmTable from "./ZmTable";
import { useZmUsers } from "./useZmUsers";
import { ZmFilterOptions } from "./types";

const zmDateDefaults = getDefaultZmDateFilters();

/*
 * SMT-1354 model: two independent things live in the URL.
 *
 * - Selection (`email`): which customer the sibling ELSTER tab works on.
 *   Set by expanding a row, a manually typed email filter, an external link
 *   (Alle Mandanten / the customer drawer), and cleared by Load all. Read by
 *   the list only on arrival from outside the ZM tabs.
 * - List state (`filterEmail`, `dateFrom`, `dateTo`, `frequency`, `status`,
 *   `missingVatId`, `page`): every list interaction — see
 *   `listQueryParams.ts`. Expanding a row never touches it, so returning
 *   from ELSTER leaves the working list exactly as it was.
 *
 * `seedZmListState` resolves both into this component's initial state once,
 * on mount; `toZmListQuery` mirrors the list state back into the URL.
 */
const ZmView = () => {
  const withinTabs = useIsWithinTabs();
  const [query, setQuery] = useQueryParams({
    [EMAIL_PARAM_NAME]: StringParam,
    ...ZM_LIST_QUERY_CONFIG,
  });

  // Seed once from the URL as it was on mount; later URL changes (all
  // driven by this component itself) must not re-seed the list.
  const [seed] = useState(() =>
    seedZmListState(query, {
      emailParam: query.email,
      withinTabs,
      defaults: zmDateDefaults,
    })
  );

  const [filterOptions, setFilterOptions] = useState<ZmFilterOptions>(
    seed.filterOptions
  );

  const { users, loading, page, hasUsers, pagination } = useZmUsers(
    filterOptions,
    { initialPage: seed.page }
  );

  useEffect(() => {
    // An arrival with a selected customer (`?email=`, no `filterEmail`) seeds
    // the list to them, same as today — but that seed only lives in local
    // state. Mirror it into `filterEmail` so a later return from a sibling
    // tab (which sets `withinTabs` but never touches `filterEmail`) still
    // finds the list filtered to this customer, instead of coming back
    // empty. `replaceIn` so this doesn't add its own history entry.
    if (seed.filterOptions.email && !query.filterEmail) {
      setQuery({ filterEmail: seed.filterOptions.email }, "replaceIn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL's `page` in sync with the hook's own page state (manual
  // pagination via Previous/Next) — a filter change already writes `page`
  // itself (always back to 1), see `handleFilterChange`.
  useEffect(() => {
    setQuery({ page: page > 1 ? page : undefined }, "replaceIn");
  }, [page, setQuery]);

  // ZmFilters always spreads the full filterOptions into onFilterChange
  // (`{ ...filterOptions, ...updated }`), so `updated` carries `email` on
  // every call — including a plain status/date/frequency change that never
  // touched the email box. Only write `email` to the URL when it actually
  // changed vs. the list's current email, or a status/date/frequency edit
  // after an expand would wipe the `email` selection param (SMT-1354). Read
  // the current value off a ref so handleFilterChange stays referentially
  // stable.
  const filterOptionsRef = useRef(filterOptions);
  filterOptionsRef.current = filterOptions;

  const handleFilterChange = useCallback(
    (updated: Partial<ZmFilterOptions>) => {
      const merged: ZmFilterOptions = {
        ...filterOptionsRef.current,
        ...updated,
      };

      const emailChanged =
        "email" in updated &&
        (updated.email ?? "") !== filterOptionsRef.current.email;

      setQuery({
        ...toZmListQuery(merged, 1, zmDateDefaults),
        ...(emailChanged ? { email: merged.email || undefined } : {}),
      });

      setFilterOptions(merged);
    },
    [setQuery]
  );

  const handleLoadAll = useCallback(() => {
    setQuery({
      [EMAIL_PARAM_NAME]: undefined,
      filterEmail: undefined,
      page: undefined,
    });
    setFilterOptions((prev) => ({ ...prev, email: "" }));
  }, [setQuery]);

  const handleSelectCustomer = useCallback(
    (email: string) => {
      setQuery({ [EMAIL_PARAM_NAME]: email });
    },
    [setQuery]
  );

  return (
    <ContentWrapper>
      <SubNavigation activeTab={ZM_TABS.ZM.key} />
      <ZmFilters
        filterOptions={filterOptions}
        datePickerDefaults={seed.datePickerDefaults}
        onFilterChange={handleFilterChange}
        onLoadAll={handleLoadAll}
      />
      <ZmTable
        users={users}
        loading={loading}
        filterOptions={filterOptions}
        page={page}
        hasUsers={hasUsers}
        onSelectCustomer={handleSelectCustomer}
        onPreviousPage={pagination.goToPrevious}
        onNextPage={pagination.goToNext}
      />
    </ContentWrapper>
  );
};

export default ZmView;
